import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Pre-screening Calls — right-rail card on the ticket detail (§22.1 / #4).
 *
 * Surfaces FULL call details that the previous build only persisted but never
 * exposed in the UI: contact name + phone, duration, notes, conducted-by,
 * occurred-at. List sorted most-recent-first. Each entry can be expanded
 * inline to read the full notes. Within the 5-min edit window the call's
 * author sees an Edit button.
 *
 * Data: GET /tickets/{id}/prescreen-calls (already exists). The card lazily
 * loads only when this ticket is being viewed; cached 60s via TanStack Query.
 * Re-fetches on POST/PATCH via the form's onSuccess invalidation.
 *
 * Permission gating:
 *   - Render the card whenever the user can read the ticket (it's internal
 *     context, never customer-facing — Customer Users never see /tickets/:id
 *     in the first place; the card is hidden on the portal).
 *   - The "Log new call" + "Edit" controls are gated on
 *     ticket.comment.internal (matches the backend require_permission).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { hasPerm } from '@/store/auth';
import { PrescreeningCallForm } from './PrescreeningCallForm';
const OUTCOME_LABEL = {
    reached: 'Customer reached',
    no_answer: 'No answer',
    voicemail: 'Voicemail left',
    callback_scheduled: 'Callback scheduled',
    declined: 'Declined',
    other: 'Other',
};
const OUTCOME_TONE = {
    reached: 'text-emerald-700 dark:text-emerald-300',
    no_answer: 'text-amber-700 dark:text-amber-300',
    voicemail: 'text-amber-700 dark:text-amber-300',
    callback_scheduled: 'text-sky-700 dark:text-sky-300',
    declined: 'text-rose-700 dark:text-rose-300',
    other: 'text-slate-600 dark:text-slate-400',
};
const NOTES_TRUNCATE = 80;
// Mirrors backend EDIT_WINDOW_MINUTES — kept here so the Edit button hides
// once stale. The backend enforces this authoritatively (returns 403 on
// stale PATCH); this is a UX guard.
const EDIT_WINDOW_MS = 5 * 60 * 1000;
function formatWhen(iso) {
    // Match the existing dual-TZ display convention used elsewhere — keep the
    // primary in the viewer's TZ, suffixed with the short tz name.
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat(undefined, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(d);
    }
    catch {
        return iso;
    }
}
function truncate(text, n) {
    if (text.length <= n)
        return { head: text, tail: null };
    // Snap to the nearest space for readability.
    let cut = n;
    while (cut > 0 && text.charAt(cut) !== ' ' && cut > n - 12)
        cut--;
    if (cut <= 0)
        cut = n;
    return { head: text.slice(0, cut).trimEnd(), tail: text.slice(cut).trimStart() };
}
/** Small "📞 N" badge shown next to the ticket number when ≥1 prescreening
 *  call has been logged. Tooltip explains. Hidden at count = 0 so legacy
 *  tickets without calls render unchanged. Shares the TanStack Query cache
 *  key with PrescreeningCallsCard so this is NOT a duplicate fetch — the two
 *  components read the same cached list. */
export function PrescreeningCallsBadge({ ticketId }) {
    const calls = useQuery({
        queryKey: ['ticket', ticketId, 'prescreen-calls'],
        queryFn: () => api.get(`/tickets/${ticketId}/prescreen-calls`),
        staleTime: 60_000,
    });
    const count = calls.data?.length ?? 0;
    if (count === 0)
        return null;
    return (_jsxs("span", { title: `${count} pre-screening call${count > 1 ? 's' : ''} logged — see the right rail card for details`, className: "inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200", children: ["\uD83D\uDCDE ", count] }));
}
export function PrescreeningCallsCard({ ticketId, me, ticketStatus, hasPendingEngineerApproval, }) {
    const [logFormOpen, setLogFormOpen] = useState(false);
    const [expandedCallIds, setExpandedCallIds] = useState(new Set());
    const [editingCallId, setEditingCallId] = useState(null);
    const canLog = hasPerm(me, 'ticket.comment.internal');
    // Gating: the ticket must be live (open/in_progress) AND there must be a
    // field engineer awaiting SDA approval. Defaults to OFF if a caller hasn't
    // supplied the props yet (back-compat with any usage that doesn't pass them).
    const terminal = ticketStatus
        ? ['resolved', 'closed', 'cancelled'].includes(ticketStatus)
        : false;
    const inWindow = !terminal && !!hasPendingEngineerApproval;
    const calls = useQuery({
        queryKey: ['ticket', ticketId, 'prescreen-calls'],
        queryFn: () => api.get(`/tickets/${ticketId}/prescreen-calls`),
        staleTime: 60_000,
    });
    const items = calls.data ?? [];
    const count = items.length;
    const toggleExpand = (id) => {
        setExpandedCallIds((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const canEdit = (c) => {
        if (!canLog)
            return false;
        if (!me?.user?.id)
            return false;
        if (c.conducted_by_user_id !== me.user.id)
            return false;
        const ageMs = Date.now() - new Date(c.created_at).getTime();
        return ageMs >= 0 && ageMs <= EDIT_WINDOW_MS;
    };
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm font-semibold", children: ["\uD83D\uDCDE Pre-screening Calls", count > 0 && (_jsx("span", { className: "rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300", children: count }))] }), canLog && inWindow && !logFormOpen && (_jsx(Button, { size: "sm", variant: "outline", onClick: () => setLogFormOpen(true), children: "+ Log new call" }))] }) }), _jsxs(CardBody, { className: "space-y-3", children: [calls.isLoading && (_jsx("div", { className: "text-xs text-slate-500", children: "Loading calls\u2026" })), calls.isError && (_jsx("div", { className: "rounded-md bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: "Could not load calls. Try refreshing the page." })), !calls.isLoading && !calls.isError && count === 0 && !logFormOpen && (_jsx("div", { className: "text-xs text-slate-500", children: inWindow ? (_jsxs(_Fragment, { children: ["No calls logged yet.", canLog && _jsx(_Fragment, { children: " Use the button above to record one." })] })) : (_jsxs(_Fragment, { children: ["Pre-screening calls are logged once a vendor proposes a field engineer", terminal ? '' : ' (after dispatch)', ". Past calls on this ticket will appear here."] })) })), logFormOpen && (_jsx(PrescreeningCallForm, { ticketId: ticketId, alwaysOpen: true, onDone: () => setLogFormOpen(false) })), items.map((c) => {
                        const expanded = expandedCallIds.has(c.id);
                        const editing = editingCallId === c.id;
                        const notes = (c.notes ?? '').trim();
                        const { head, tail } = notes ? truncate(notes, NOTES_TRUNCATE) : { head: '', tail: null };
                        const showEdit = canEdit(c);
                        if (editing) {
                            return (_jsx(PrescreeningCallForm, { ticketId: ticketId, existingCall: c, alwaysOpen: true, onDone: () => setEditingCallId(null) }, c.id));
                        }
                        return (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-white p-3 text-xs dark:border-slate-800 dark:bg-slate-900/40", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("div", { className: "text-slate-700 dark:text-slate-200", children: [c.conducted_by_email ? (_jsx("span", { className: "font-medium", children: c.conducted_by_email.split('@')[0] || 'Unknown' })) : (_jsx("span", { className: "font-medium text-slate-500", children: "Unknown" })), _jsxs("span", { className: "ml-1 text-slate-500", children: ["\u00B7 ", formatWhen(c.occurred_at)] })] }), _jsx("button", { type: "button", onClick: () => toggleExpand(c.id), className: "rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800", "aria-label": expanded ? 'Collapse details' : 'Expand details', "aria-expanded": expanded, children: expanded ? '▲' : '▼' })] }), _jsxs("div", { className: "mt-1", children: [_jsx("span", { className: `font-medium ${OUTCOME_TONE[c.outcome] ?? ''}`, children: OUTCOME_LABEL[c.outcome] ?? c.outcome }), c.duration_minutes != null && (_jsxs("span", { className: "ml-2 text-slate-500", children: [c.duration_minutes, " min"] }))] }), !expanded && notes && (_jsxs("div", { className: "mt-1 text-slate-600 dark:text-slate-300", children: ["\u201C", head, tail ? '…' : '', "\u201D"] })), expanded && (_jsxs("div", { className: "mt-2 space-y-1 border-t border-slate-100 pt-2 dark:border-slate-800", children: [c.contact_name && (_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Contact name:" }), ' ', _jsx("span", { className: "text-slate-700 dark:text-slate-200", children: c.contact_name })] })), c.contact_phone && (_jsxs("div", { children: [_jsx("span", { className: "text-slate-500", children: "Phone:" }), ' ', _jsx("span", { className: "text-slate-700 dark:text-slate-200", children: c.contact_phone })] })), notes && (_jsxs("div", { children: [_jsx("div", { className: "text-slate-500", children: "Notes:" }), _jsx("div", { className: "whitespace-pre-wrap text-slate-700 dark:text-slate-200", children: notes })] })), !notes && !c.contact_name && !c.contact_phone && (_jsx("div", { className: "italic text-slate-400", children: "No additional details captured." })), c.updated_at !== c.created_at && (_jsxs("div", { className: "text-[10px] italic text-slate-400", children: ["Edited at ", formatWhen(c.updated_at)] })), showEdit && (_jsx("div", { className: "pt-1", children: _jsx(Button, { size: "sm", variant: "outline", onClick: () => setEditingCallId(c.id), children: "\u270F Edit (within 5 min)" }) }))] }))] }, c.id));
                    })] })] }));
}
